import { useTranslation } from 'react-i18next';

import { EmailMarketingContainer } from '@/components/Containers/EmailMarketingContainer';
import { ACTIVE, PENDING } from '@/config/appConstants';
import { Country } from '@/features/countries';
import { SmsCredits } from '@/features/smsCredits';
import { BillingMode, iSteps, ProductTypes, UserProductTypes } from '@/types';
import {
  filterUserProductsByStatus,
  getBillingModeFromUserProduct,
  getKeyByValue,
  getProductStep,
} from '@/utils/helpers';

export type MarketingProductsProps = {
  userIsLogged?: boolean;
  onRequestInfo: (product: ProductTypes) => void;
  onProductSelect: (product: ProductTypes) => void;
  onFreeProductSelect?: (product: ProductTypes) => void;
  products: ProductTypes[][];
  userProducts?: UserProductTypes[];
  steps: iSteps;
  countries?: Country[];
  defaultSMSCountryProvider?: string;
  smsCredits?: SmsCredits;
};

export interface iEmailMarketingProductsProps extends MarketingProductsProps {
  freemiumProduct: ProductTypes;
  showFeatures?: boolean;
  stickyMode?: boolean;
  publicPage?: boolean;
}

export const EmailMarketingProducts = ({
  userIsLogged,
  onRequestInfo,
  onProductSelect,
  onFreeProductSelect,
  freemiumProduct,
  products,
  userProducts,
  steps,
  showFeatures = true,
  stickyMode = false,
  publicPage,
}: iEmailMarketingProductsProps) => {
  const { t } = useTranslation();
  const title = t('productTypes.emailMarketing.headerTitle');
  const marks = steps;
  const max = Object.keys(marks).length - 1;

  const onSliderChange = () => {};

  const onDefaultFreeProductSelect = (product: ProductTypes) => {
    onFreeProductSelect && onFreeProductSelect(product);
  };

  const getDefaultSliderValue = () => {
    if (!userProducts || userProducts.length === 0) {
      return 0;
    }
    const userProductActive = filterUserProductsByStatus(userProducts || [], ACTIVE);
    const userProductPending = filterUserProductsByStatus(userProducts || [], PENDING);
    const pro = userProductPending || userProductActive;
    if (!pro) return 0;

    const currentStep = getKeyByValue(marks, getProductStep(pro));

    if (!currentStep) {
      return 0;
    }

    return parseInt(currentStep, 10);
  };

  const getDefaultCheckedBillingModeValue = (): BillingMode => {
    const userProductActive = filterUserProductsByStatus(userProducts || [], ACTIVE);
    const userProductPending = filterUserProductsByStatus(userProducts || [], PENDING);

    return getBillingModeFromUserProduct(userProductPending || userProductActive);
  };

  return (
    <EmailMarketingContainer
      userIsLogged={userIsLogged}
      title={title}
      freemiumProduct={freemiumProduct}
      products={products}
      userProducts={userProducts}
      max={max}
      onSliderChange={onSliderChange}
      onProductSelect={onProductSelect}
      onFreeProductSelect={onDefaultFreeProductSelect}
      onRequestInfo={onRequestInfo}
      marks={marks}
      selectMarks={marks}
      defaultValue={getDefaultSliderValue()}
      showFeatures={showFeatures}
      stickyMode={stickyMode}
      publicPage={publicPage}
      billingMode={getDefaultCheckedBillingModeValue()}
    />
  );
};
